import React, { useEffect, useRef } from 'react';
import {
	popFromMediaStack,
	pushToMediaStack,
} from '@wsc/redux_stores/mediaControllerReducer/actions';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { formURL } from '@wsc/utils/apiUtils';
import { setSoundHotspot } from '@wsc/redux_stores/soundHotspotReducer';
import { extractHotspotInfo } from '@wsc/utils/sceneUtils';
import { getFilename } from '@wsc/utils/fileUtils';
import { useAnalytics } from '@wsc/hooks';

const SoundHotspotController = () => {
	const soundHotspotId = useSelector((state) => state?.soundHotspot?.id);
	const changeCount = useSelector((state) => state?.soundHotspot?.count);

	const hotspotDataSelector = createSelector(
		(state) => state.roomObjects || [],
		(roomObjects) => {
			for (let obj in roomObjects) {
				if (roomObjects[obj]._id?.$oid === soundHotspotId) {
					return roomObjects[obj];
				}
			}
		},
	);

	const soundHotspotData = useSelector(hotspotDataSelector);
	const audioUrl = formURL(soundHotspotData?.props?.url);

	const audioRef = useRef();
	const dispatch = useDispatch();

	const onCanPlay = () => {
		audioRef.current.play().then(() => {
			dispatch(pushToMediaStack(audioRef));
		});
	};

	const { collect } = useAnalytics();

	const handleSoundCollect = () => {
		const hotspotInfo = extractHotspotInfo(soundHotspotData.props);
		collect({
			event_name: 'click_content',
			action: 'Sound hotspot clicked',
			content_name: getFilename(audioUrl),
			content_URL: audioUrl,
			...hotspotInfo,
		});
	};

	const onEnded = () => {
		dispatch(popFromMediaStack());
		dispatch(setSoundHotspot(null));
	};

	useEffect(() => {
		audioRef.current.addEventListener('ended', onEnded);
		audioRef.current.addEventListener('loadedmetadata', onCanPlay);

		return () => {
			audioRef.current &&
				audioRef.current.removeEventListener('ended', onEnded);
			audioRef.current &&
				audioRef.current.removeEventListener('canplay', onCanPlay);
		};
	}, []);

	useEffect(() => {
		if (audioUrl) {
			handleSoundCollect();
		}
		if (audioRef.current.src && audioRef.current.src !== audioUrl) {
			audioRef.current.src = audioUrl;
		} else {
			audioRef.current.src = null;
			onEnded();
		}
	}, [audioUrl, changeCount]);

	return <audio ref={audioRef} autoPlay={false} />;
};

export default SoundHotspotController;
